import * as React from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import { Translate, translateDateFormat } from '../../../../shared/LanguageService';
import { useCancellationTimingQuery } from '../queries/useCancellationTimingQuery';
import { useCancelBookingMutation } from '../queries/useCancelBookingMutation';
import { CancellationTiming, CustomerBooking } from '../../types';

type CancelBookingDialogProps = {
    booking: CustomerBooking | null;
    open: boolean;
    onAbort: () => void;
    onCancelled: (booking: CustomerBooking) => void;
    onFailed: () => void;
};

const TitleId = 'cancel-booking-dialog-title';

function costWarning(timing: CancellationTiming | undefined): string | null {
    if (timing === CancellationTiming.Late) {
        return Translate('view-order/basic-order-info/unbook/modal/message/late');
    }
    if (timing === CancellationTiming.SameDay || timing === CancellationTiming.AfterBookingDay) {
        return Translate('view-order/basic-order-info/unbook/modal/message/sameday');
    }
    return null;
}

function bookedTime(booking: CustomerBooking): string {
    let start = new Date(booking.start);
    return `${translateDateFormat(start, 'PPPP')} ${translateDateFormat(start, 'HH:mm')}`;
}

function CancelBookingDialog({
    booking,
    open,
    onAbort,
    onCancelled,
    onFailed
}: CancelBookingDialogProps) {
    const timing = useCancellationTimingQuery(open ? booking : null);
    const cancelBooking = useCancelBookingMutation();
    const warning = costWarning(timing.data);

    function confirm() {
        cancelBooking
            .mutateAsync(booking!)
            .then(() => onCancelled(booking!))
            .catch(() => onFailed());
    }

    return (
        <Dialog open={open} onClose={onAbort} fullWidth maxWidth="xs" aria-labelledby={TitleId}>
            {booking && (
                <>
                    <DialogTitle id={TitleId}>
                        {Translate('customer-calendar/cancel/title')}
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body1">
                            {Translate('customer-calendar/cancel/question', {
                                address: booking.address || '',
                                time: bookedTime(booking)
                            })}
                        </Typography>
                        {warning && (
                            <Alert severity="warning" sx={{ mt: 2 }}>
                                {warning}
                            </Alert>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={onAbort}>
                            {Translate('view-order/basic-order-info/unbook/modal/cancel-button')}
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={confirm}
                            disabled={timing.isLoading || cancelBooking.isLoading}
                        >
                            {Translate('view-order/basic-order-info/unbook/modal/unbook-button')}
                        </Button>
                    </DialogActions>
                </>
            )}
        </Dialog>
    );
}

export default CancelBookingDialog;
